import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FormInput, FormSelect } from '../finance';
import { X, CircleNotch } from '@phosphor-icons/react';
import transportService from '../../services/transportService';
import './transport-forms.css';

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'retired', label: 'Retired' }
];

function VehicleForm({ vehicle, onSave, onClose }) {
  const [formData, setFormData] = useState(vehicle || {
    registration_number: '',
    vehicle_type: 'bus',
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    seating_capacity: 60,
    status: 'active',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingTypes(true);
        const data = await transportService.getVehicleTypes();
        console.log('Fetched vehicle types:', data);
        setVehicleTypes(Array.isArray(data) ? data : []);
        
        // Set the first vehicle type if formData doesn't have a valid one
        if ((!formData.vehicle_type || formData.vehicle_type === '') && Array.isArray(data) && data.length > 0) {
          setFormData(prev => ({
            ...prev,
            vehicle_type: data[0].value
          }));
        }
      } catch (error) {
        console.error('Error fetching vehicle types:', error);
        toast.error('Failed to load vehicle types: ' + error.message);
        // Fallback to default types
        const defaultTypes = [
          { value: 'bus', label: 'Bus' },
          { value: 'van', label: 'Van' },
          { value: 'minibus', label: 'Mini Bus' },
          { value: 'shuttle', label: 'Shuttle' }
        ];
        setVehicleTypes(defaultTypes);
      } finally {
        setLoadingTypes(false);
      }
    };

    fetchVehicleTypes();
  }, [formData.vehicle_type]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value) : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.registration_number) {
      toast.error('Registration number is required');
      return;
    }
    if (!formData.make || !formData.model) {
      toast.error('Make and model are required');
      return;
    }

    setLoading(true);
    try {
      await onSave(vehicle?.id, formData);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2>{vehicle ? 'Edit Vehicle' : 'Add New Vehicle'}</h2>
          <button onClick={onClose} className="btn-close"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          <div className="form-row">
            <FormInput
              label="Registration Number"
              name="registration_number"
              value={formData.registration_number}
              onChange={handleChange}
              required
              placeholder="e.g., GV-2426-AA"
            />
            <FormSelect
              label="Vehicle Type"
              name="vehicle_type"
              value={formData.vehicle_type}
              onChange={handleChange}
              options={vehicleTypes}
              placeholder="Select Vehicle Type"
              disabled={loadingTypes}
            />
          </div>

          <div className="form-row">
            <FormInput label="Make" name="make" value={formData.make} onChange={handleChange} required />
            <FormInput label="Model" name="model" value={formData.model} onChange={handleChange} required />
          </div>

          <div className="form-row">
            <FormInput label="Year" name="year" type="number" value={formData.year} onChange={handleChange} min="2000" />
            <FormInput label="Color" name="color" value={formData.color} onChange={handleChange} />
          </div>

          <div className="form-row">
            <FormInput
              label="Seating Capacity"
              name="seating_capacity"
              type="number"
              value={formData.seating_capacity}
              onChange={handleChange}
              min="1"
            />
            <FormSelect
              label="Status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              options={STATUS_OPTIONS}
            />
          </div>

          <FormInput
            label="Notes"
            name="notes"
            as="textarea"
            value={formData.notes}
            onChange={handleChange}
            rows="3"
          />

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <span className="button-loading">
                  <CircleNotch size={16} weight="fill" className="spinner" />
                  Saving...
                </span>
              ) : vehicle ? 'Update Vehicle' : 'Create Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VehicleForm;
